// Operator-side list card for /tarjouspyynnot. Visual recipe mirrors
// ValuListCard (FrostedCard + thumbnail + KeikkaPumpIcon overlay + amber CTA).
// Tile background priority: customer's first image attachment (SAS url from
// provider-list) → Google Static Map of the address TEXT the card already
// shows. The provider list intentionally carries NO lat/lng and the address
// arrives pre-masked (street number stripped) until the caller's offer is
// accepted/confirmed — mapping the visible text therefore leaks nothing new.
// Masked rows get an area map (zoom 13, no marker); revealed rows get the
// unmasked street address with a marker (zoom 14).

import { useMemo } from "react";
import { Box, Button, Chip, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router";
import FrostedCard from "src/components/quote/FrostedCard";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { formatSentDate, formatNettoEuros, formatDistanceKm } from "src/utils/valut/valutFormatters";
import { useLang, useT } from "src/i18n";
import KeikkaPumpIcon from "src/components/valut/KeikkaPumpIcon";
import { inferVehicleType } from "src/components/valut/inferVehicleType";
import { addressMapUrl } from "src/utils/valut/staticMapUrl";
import { kayttokohdeOptions, labelFor } from "src/utils/quote/quoteConstants";
import { offerStateLabel } from "@ibetoni/jerry-client";

function describePump({ requiredPuomi, requiredLinja }, t) {
    const p = Number(requiredPuomi);
    if (Number.isFinite(p) && p > 0) return t("provider.listCard.pumpBoomSuffix", { m: p });
    const l = Number(requiredLinja);
    if (Number.isFinite(l) && l > 0) return t("provider.listCard.pumpLineSuffix", { m: l });
    return null;
}

export default function TarjouspyyntoListCard({ row }) {
    const theme = useTheme();
    const { lang } = useLang();
    // `t` was threaded through by Task 18 purely so the shared KAYTTOKOHDE_OPTIONS
    // array (converted to a function of `t`, see quoteConstants.js) renders a
    // translated label instead of forced Finnish; Task 19 then extracted this
    // file's own remaining Finnish literals using the same `t`.
    // Memoized: this card renders once per row in a list.
    const t = useT();
    const kkOptions = useMemo(() => kayttokohdeOptions(t), [t]);
    const w = theme.custom?.wizard ?? {};
    const { isPumi } = inferVehicleType({ m3: row.totalM3, puomi: row.requiredPuomi });
    const thumbUrl = row.thumbnail?.fileType?.startsWith("image/") ? row.thumbnail.url : null;
    // Same reveal derivation as the backend (provider-list): own offer accepted/confirmed.
    const isRevealed = row.myOfferState === "accepted" || row.myOfferState === "confirmed";
    const address = row.formattedAddress || row.osoite;
    const mapUrl = !thumbUrl
        ? addressMapUrl({ address, w: 240, h: 160, zoom: isRevealed ? 14 : 13, marker: isRevealed })
        : null;
    const bgUrl = thumbUrl || mapUrl;
    const pumpDesc = describePump(row, t);
    const stateLabel = offerStateLabel(row.myOfferState);

    return (
        <FrostedCard
            component={RouterLink}
            to={`/tarjouspyynnot/${row.pumppuRequestId}`}
            sx={{
                p: 2,
                textDecoration: "none",
                color: "inherit",
                display: "block",
                transition: "transform 120ms ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "stretch" }}>
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: "100%", sm: 120 },
                        height: { xs: 140, sm: 80 },
                        flexShrink: 0,
                        borderRadius: w.fieldRadius ? `${w.fieldRadius}px` : "10px",
                        overflow: "hidden",
                        bgcolor: "rgba(244,242,238,0.04)",
                        backgroundImage: bgUrl ? `url("${bgUrl.replace(/"/g, "%22")}")` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                    role="img"
                    aria-label={
                        thumbUrl ? t("provider.listCard.sitePhotoAlt")
                            : mapUrl ? t(
                                isRevealed ? "provider.listCard.mapAltRevealed" : "provider.listCard.mapAltMasked",
                                { address }
                            )
                                : t("provider.listCard.noImageAlt")
                    }
                >
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                            filter: "drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                        }}
                    >
                        <KeikkaPumpIcon
                            m3={row.totalM3}
                            puomi={row.requiredPuomi}
                            linjanPituus={row.requiredLinja}
                            isPumi={isPumi}
                            size={48}
                            noAnimation
                            invertMode="light"
                        />
                    </Box>
                </Box>

                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontFamily: "'Black Ops One', sans-serif", letterSpacing: 0.5 }}>
                        {row.formattedAddress || row.osoite}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {[
                            labelFor(kkOptions, row.kayttokohde, row.kayttokohde),
                            row.totalM3 ? `${row.totalM3} m³` : null,
                            pumpDesc,
                        ].filter(Boolean).join(" · ")}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {t("provider.listCard.receivedPrefix", { date: formatSentDate(row.createdAt, lang) })}
                        </Typography>
                        {row.myOffer && stateLabel && (
                            <Chip
                                size="small"
                                label={`${stateLabel} · ${formatNettoEuros(row.myOffer.priceCents, lang)}`}
                                sx={{ ml: 1, bgcolor: w.amber ?? "warning.main", color: "background.default", fontWeight: 600 }}
                            />
                        )}
                        {/* distanceKm = closest delivery-capable varikko; present on ALL operator tabs. */}
                        {row.distanceKm != null && (
                            <Tooltip title={t("provider.listCard.distanceTooltip")}>
                                <Chip size="small" variant="outlined" label={formatDistanceKm(row.distanceKm, lang)} />
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <Stack sx={{ alignItems: { xs: "stretch", sm: "flex-end" }, justifyContent: "flex-end" }}>
                    <Button variant="contained" sx={amberCtaSx} component="span">
                        {t("provider.listCard.viewCta")}
                    </Button>
                </Stack>
            </Stack>
        </FrostedCard>
    );
}
